// src/pages/articles/ArticlePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getArticleBySlug,
  getBookmarkCount,
  getLikeCount,
  getRelatedArticles,
  incrementViews,
  isBookmarked,
  isLiked,
  toggleBookmark,
  toggleLike,
} from "../../features/articles/api";

import "../../shared/styles/pages/ArticlePage.css";
import Breadcrumbs from "../../shared/components/Breadcrumbs";
import CodeBlock from "../../features/articles/components/CodeBlock";
// import ArticleChatWidget from "./components/ArticleChatWidget";

import { useAuth } from "../../shared/auth/AuthProvider";

import { Row, Col, Card, Tabs, Tab, Badge, Modal, Carousel, Image, Button, Form, NavDropdown, Navbar, Nav, Container, Spinner } from 'react-bootstrap'
import {
  BsStarFill,
  BsStarHalf,
  BsStar,
  BsEye,
  BsBookmark,
  BsBookmarkFill,
  BsTerminal,
  BsCpu,
  BsAirplane,
  BsRobot,
  BsChatSquareHeart,
  BsFiletypePdf,
  BsClock,
  BsHeart,
  BsHeartFill,
} from "react-icons/bs";

// ⬇️ เปลี่ยนมาใช้ public profile
import { getPublicProfileByUid } from "../../features/articles/api";
import RecommendedArticleList from "../../features/articles/components/RecommendedArticleList";

// Dynamic Heading: ใช้ level 1–6 เพื่อเลือก h1–h6
function H({ level = 2, className = "", children, ...props }) {
  const safeLevel = [1, 2, 3, 4, 5, 6].includes(level) ? level : 2;
  const Tag = `h${safeLevel}`;
  return (
    <Tag className={className} {...props}>
      {children}
    </Tag>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  const { user } = useAuth();
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [bookmarkCount, setBookmarkCount] = useState(0);

  const [authorProfile, setAuthorProfile] = useState(null);

  const [showLoginModal, setShowLoginModal] = useState(false);

  // const crumbs = useMemo(() => (...), [article]);

  useEffect(() => {
    let alive = true;

    async function run() {
      setLoading(true);

      const a = await getArticleBySlug(slug);
      if (!alive) return;
      setArticle(a);

      // --- ดึง public profile ผู้เขียนจาก publicProfiles/{uid} ---
      if (a?.author?.uid) {
        getPublicProfileByUid(a.author.uid)
          .then((p) => {
            if (!alive) return;
            setAuthorProfile(p); // ถ้าไม่มี doc -> p = null
          })
          .catch((err) => {
            console.error("load public profile failed", err);
            if (!alive) return;
            setAuthorProfile(null);
          });
      } else {
        setAuthorProfile(null);
      }

      // นับจำนวน views (fire-and-forget)
      if (
        a?.id &&
        a.status === "published" &&
        a.publicStatus === true
      ) {
        incrementViews(a.id);
      }

      // บทความที่เกี่ยวข้อง + ตั้ง title
      if (a) {
        const r = await getRelatedArticles({
          subCategorySlug: a.subCategorySlug, // ✅
          excludeId: a.id,
          limit: 6,
        });
        if (!alive) return;
        setRelated(r);
        document.title = `${a.title} | Encraft`;
      }

      setLoading(false);
    }

    run();
    return () => {
      alive = false;
    };
  }, [slug]);

   // useEffect แรก: โหลดบทความ (ของเดิม)

  // useEffect ที่สอง: โหลดสถานะ like/bookmark
  useEffect(() => {
    if (!article || !article.id) return;

    async function loadStates() {
      try {
        const [likeCnt, bmCnt] = await Promise.all([
          getLikeCount(article.id),
          getBookmarkCount(article.id),
        ]);

        setLikeCount(likeCnt);
        setBookmarkCount(bmCnt);

        if (user) {
          const [likedRes, bookmarkedRes] = await Promise.all([
            isLiked(article.id, user.uid),
            isBookmarked(article.id, user.uid),
          ]);
          setLiked(likedRes);
          setBookmarked(bookmarkedRes);
        } else {
          setLiked(false);
          setBookmarked(false);
        }
      } catch (err) {
        console.error("load like/bookmark state failed", err);
      }
    }

    loadStates();
  }, [article, user]);


  if (loading)
    return <div className="text-center my-4"><Spinner animation="border" role="status" /></div>;
  if (!article)
    return <div className="container py-5">❌ ไม่พบบทความนี้</div>;

  // รวมเนื้อหาที่เป็น heading / paragraph ไว้ส่งให้ AI
  const articleTextContent = Array.isArray(article.contentBlocks)
    ? article.contentBlocks
        .filter(
          (b) =>
            b.type === "heading" || b.type === "paragraph"
        )
        .map((b) => b.text)
        .join("\n\n")
    : "";

  // แปลง createdAt เป็น Date
  let createdDate = null;
  if (article.createdAt) {
    if (typeof article.createdAt.toDate === "function") {
      createdDate = article.createdAt.toDate();
    } else if (article.createdAt instanceof Date) {
      createdDate = article.createdAt;
    }
  }

  const displayDate = formatThaiDate(createdDate);

  function formatThaiDate(date) {
    if (!date) return "";
    return date.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      // year: "numeric",
    });
  }

  // --- helper สำหรับ avatar + ชื่อ ---
  const authorPhotoURL =
    authorProfile?.photoURL || article.author?.photoURL || "";
  const authorDisplayName =
    authorProfile?.displayName ||
    article.author?.displayName ||
    "ผู้เขียน";

  const authorInitial = (authorDisplayName[0] || "E")
    .toUpperCase();


 
  const handleToggleLike = async () => {
  if (!user) {
    setShowLoginModal(true);
    // alert("กรุณาเข้าสู่ระบบก่อนกดถูกใจบทความ");
    return;
  }
  if (!article || !article.id) return;

  try {
    const nowLiked = await toggleLike(article.id, user.uid);
    setLiked(nowLiked);
    const likeCnt = await getLikeCount(article.id);
    setLikeCount(likeCnt);
  } catch (err) {
    console.error("toggle like failed", err);
  }
};

const handleToggleBookmark = async () => {
  if (!user) {
    setShowLoginModal(true);
    // alert("กรุณาเข้าสู่ระบบก่อนบันทึกบทความ");
    return;
  }
  if (!article || !article.id) return;

  try {
    const nowBm = await toggleBookmark(article.id, user.uid);
    setBookmarked(nowBm);
    const bmCnt = await getBookmarkCount(article.id);
    setBookmarkCount(bmCnt);
  } catch (err) {
    console.error("toggle bookmark failed", err);
  }
};

  return (
    <div className="article-page-wrapper">
      <div className="container pt-0 pb-4">
        <div className="article-page">
      {/* <Breadcrumbs items={crumbs} /> */}

      {/* หัวเรื่อง */}
      <header className="article-header">
        <h1 className="article-title">{article.title}</h1>

        {/* subtitle สไตล์ Medium */}
        {article.subtitle && article.subtitle.trim() !== "" && (
          <p className="article-subtitle">
            {article.subtitle}
          </p>
        )}

        <div className="article-meta text-muted d-flex align-items-center gap-2">
          {/* avatar ผู้เขียน */}
          <Link
            to={`/search?q=${encodeURIComponent(authorDisplayName)}`}
            className="article-author-link d-flex align-items-center gap-2 text-decoration-none text-reset"
          >
            <div className="article-author-avatar-wrapper">
              {authorPhotoURL ? (
                <img
                  src={authorPhotoURL}
                  alt={authorDisplayName}
                  className="article-author-avatar"
                />
              ) : (
                <div className="article-author-avatar placeholder">
                  {authorInitial}
                </div>
              )}
            </div>
          </Link>

          {/* ชื่อ + meta */}
          <div>
            <Link
            to={`/search?q=${encodeURIComponent(authorDisplayName)}`}
            className="text-decoration-none text-reset"
            >
              <span>{authorDisplayName}</span>
            </Link>
            {displayDate && (
              <>
                <span className="mx-2">•</span>
                <span>{displayDate}</span>
              </>
            )}
           {/*  {typeof article.readTime === "number" && (
              <>
                <span className="mx-2">•</span>
                <span>
                  {article.readTime} min read
                </span>
              </>
            )} */}
            {typeof article.views === "number" && (
              <>
                <span className="mx-2">•</span>
                <span>
                  {article.views.toLocaleString()} views
                </span>
              </>
            )}
          </div>
        </div>

         {/* 🔹 แถบปุ่ม Like / Bookmark 
        <div className="article-actions d-flex align-items-center gap-2 mt-3">
          <button
            type="button"
            className={
              "btn btn-sm " + (liked ? "btn-danger" : "btn-outline-dark")
            }
            onClick={handleToggleLike}
          >
            {liked ? <BsHeartFill size={16} className="icon-bs-adjust" /> : <BsHeart size={16} className="icon-bs-adjust" />}{" "}
            {liked ? "เลิกถูกใจ" : "ถูกใจ"} · {likeCount}
          </button>

          
          <button
            type="button"
            className={
              "btn btn-sm " + (bookmarked ? "btn-warning" : "btn-outline-dark")
            }
            onClick={handleToggleBookmark}
          >
            {bookmarked ? (
              <BsBookmarkFill size={16} className="icon-bs-adjust" />
            ) : (
              <BsBookmark size={16} className="icon-bs-adjust" />
            )}
            {" "}
            {bookmarked ? "บันทึกแล้ว" : "บันทึกบทความ"} · {bookmarkCount}
          </button>
        </div>
        */}

        {article.cover && (
          <figure className="article-cover mt-3">
            <img
              src={article.cover}
              alt={article.title}
              className="img-fluid rounded"
            />
            {article.coverCaption && (
              <figcaption className="text-secondary small mt-1">
                {article.coverCaption}
              </figcaption>
            )}
          </figure>
        )}
      </header>

      {/* เนื้อหา: รองรับหลาย block เหมือน Medium */}
      <section className="article-content">
        {Array.isArray(article.contentBlocks) ? (
          article.contentBlocks.map((b, i) => {
            switch (b.type) {
              case "heading": {
                const level = b.level || 2; // ถ้าไม่มี level จะ fallback เป็น H2
                return (
                  <H
                    key={i}
                    level={level}
                    className={`fw-semibold mt-4 article-h${level}`}
                  >
                    {b.text}
                  </H>
                );
              }
              case "subheading":
                return (
                  <h3 key={i} className="article-h3">
                    {b.text}
                  </h3>
                );
              case "paragraph":
                return (
                  <p key={i} className="article-paragraph">
                    {b.text}
                  </p>
                );
              case "quote":
                return (
                  <blockquote
                    key={i}
                    className="article-quote"
                  >
                    <p style={{ whiteSpace: "pre-line" }}>
                      {b.text}
                    </p>
                    {b.cite && <cite>— {b.cite}</cite>}
                  </blockquote>
                );
              case "list":
                return b.ordered ? (
                  <ol
                    key={i}
                    className="article-list"
                  >
                    {b.items?.map((it, idx) => (
                      <li key={idx}>{it}</li>
                    ))}
                  </ol>
                ) : (
                  <ul
                    key={i}
                    className="article-list"
                  >
                    {b.items?.map((it, idx) => (
                      <li key={idx}>{it}</li>
                    ))}
                  </ul>
                );
              case "image":
                return (
                  <figure
                    key={i}
                    className="article-image"
                  >
                    <img
                      src={b.src}
                      alt={b.alt || ""}
                      className="img-fluid rounded"
                    />
                    {b.caption && (
                      <figcaption className="text-secondary small mt-1">
                        {b.caption}
                      </figcaption>
                    )}
                  </figure>
                );
              case "code":
                return (
                  <CodeBlock
                    key={i}
                    code={b.code}
                    language={b.language || "javascript"}
                    filename={b.filename}
                  />
                );
              case "hr":
                return (
                  <hr
                    key={i}
                    className="article-hr"
                  />
                );
              default:
                return null;
            }
          })
        ) : (
          <p className="text-muted">ไม่มีเนื้อหา</p>
        )}
      </section>

      {/* แท็ก */}
      {/* Tags / Categories section */}
      {(article.category?.name ||
        article.subCategory?.name ||
        (Array.isArray(article.tagsDisplay) && article.tagsDisplay.length > 0)) && (
        <div className="article-tags mt-4">

          {/* Label ด้านซ้ายแบบ Medium */}
          <div className="article-tags-label mb-2">
            Tags
          </div>

          {/* กลุ่มแท็กทั้งหมด */}
          <div className="article-tags-chips">
            {/* Category */}
            {article.category?.name && (
              <Link
                to={`/search?q=${encodeURIComponent(article.category.name)}`}
                className="tag-chip tag-chip-category"
              >
                {article.category.name}
              </Link>
            )}

            {/* SubCategory */}
            {article.subCategory?.name && (
              <Link
                to={`/search?q=${encodeURIComponent(article.subCategory.name)}`}
                className="tag-chip tag-chip-subcategory"
              >
                {article.subCategory.name}
              </Link>
            )}

            {/* Tags อื่น ๆ */}
            {Array.isArray(article.tagsDisplay) &&
              article.tagsDisplay.length > 0 &&
              article.tagsDisplay.map((t, i) => (
                <Link
                  key={i}
                  to={`/search?q=${encodeURIComponent(t)}`}
                  className="tag-chip"
                >
                  {t}
                </Link>
              ))}
          </div>
        </div>
      )}
     {/*  {Array.isArray(article.tagsDisplay) &&
        article.tagsDisplay.length > 0 && (
          <div className="article-tags mt-4">
            {article.tagsDisplay.map((t, i) => (
              <Link
                key={i}
                to={`/search?q=${encodeURIComponent(t)}`}
                className="badge rounded-pill text-bg-light me-2 mb-2 text-decoration-none"
              >
                {t}
              </Link>
            ))}
          </div>
        )} */}

      {/* บทความที่คุณอาจสนใจ */}
      {related.length > 0 && (
        <RecommendedArticleList articles={related} />
      )}

      {/* Modal แจ้งให้เข้าสู่ระบบ */}
      <div
        className={`modal fade ${showLoginModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{ background: "rgba(0,0,0,0.5)" }}
        onClick={() => setShowLoginModal(false)}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">เข้าสู่ระบบ</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowLoginModal(false)}
              ></button>
            </div>

            <div className="modal-body">
              <p>กรุณาเข้าสู่ระบบหรือสมัครสมาชิกก่อนทำรายการนี้</p>
            </div>

            {/* <div className="modal-footer d-flex justify-content-between"> */}
            <div className="modal-footer">
              <a
                href="/register"
                className="btn btn-warning text-dark fw-bold"
              >
                สมัครสมาชิก
              </a>

              <a
                href="/login"
                className="btn btn-primary fw-bold"
              >
                เข้าสู่ระบบ
              </a>
            </div>
          </div>
        </div>
      </div>


      {/* AI Chat */}
      {/* <ArticleChatWidget
        articleContext={{
          id: article.id,
          slug: article.slug,
          title: article.title,
          content: articleTextContent,
        }}
      /> */}
      </div>
     </div>
     </div>
  );
}
